import { ChangeDetectionStrategy, Component, computed, effect, inject, OnInit, signal, untracked } from '@angular/core';
import { Pagination } from '@api/abstracts/models';
import { AbstractDynamicComponent } from '@components/abstracts';
import { DEFAULT_ITEMS_PER_PAGE, DEFAULT_PAGE_NUMBER, OFFSET_FROM_END } from '@components/listing-modeles/config';
import { ListingModeles, ListingModelesText, ListingModelesVideo } from '@components/listing-modeles/models';
import { IsListingModelesTextPipe, IsListingModelesVideoPipe } from '@components/listing-modeles/pipes';
import { KitchenModelCardComponent } from '@shared/components/kitchen-model-card';
import { TitleComponent } from '@shared/components/title';
import { VideoComponent } from '@shared/components/video';
import { ViewportObserverDirective } from '@shared/directives';
import { Kitchen } from '@shared/models';
import { ElasticSearchService } from '@shared/services/elastic-search';
import { PayloadModels } from '@shared/services/elastic-search/models';
import { LogsService } from '@shared/services/logs';
import { UrlParamsService } from '@shared/services/url-params';
import { debounceTime, distinctUntilChanged, take } from 'rxjs';
import { tap } from 'rxjs/operators';

@Component({
  selector: 'app-listing-modeles',
  imports: [
    KitchenModelCardComponent,
    ViewportObserverDirective,
    IsListingModelesTextPipe,
    IsListingModelesVideoPipe,
    TitleComponent,
    VideoComponent,
  ],
  templateUrl: './listing-modeles.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ListingModelesComponent extends AbstractDynamicComponent<ListingModeles> implements OnInit {
  readonly kitchens = signal<Kitchen[]>(null!);
  readonly isExtendedSearch = signal<boolean>(false);
  readonly itemsToInsert = new Map<number, ListingModelesText | ListingModelesVideo>();
  private readonly elasticSearchService = inject(ElasticSearchService);
  private readonly urlParamsService = inject(UrlParamsService);
  private readonly logs = inject(LogsService);
  private readonly params = this.urlParamsService.params;
  private readonly currentPageFromUrl = computed(() =>
    Number(this.urlParamsService.param('listing_page')() ?? DEFAULT_PAGE_NUMBER)
  );
  private firstInit = true;
  private readonly offsetFromEnd = OFFSET_FROM_END > DEFAULT_ITEMS_PER_PAGE ? DEFAULT_ITEMS_PER_PAGE : OFFSET_FROM_END;
  private totalItems = 0;
  private remainingItems = 0;
  private nbKitchensLoaded = 0;
  private isAllKitchensLoaded = false;
  private filtersPrev = '';

  constructor() {
    super();
    effect(() => {
      const params = this.params();
      if (params['reset']) {
        untracked(() => this.urlParamsService.removeParams('reset'));
        return;
      }
      const currentPage = this.currentPageFromUrl() ?? 1;
      const itemFrom = this.nbKitchensLoaded;
      const itemsToFetch = this.firstInit ? currentPage * DEFAULT_ITEMS_PER_PAGE : DEFAULT_ITEMS_PER_PAGE;
      const { filter_selection, filter_budget, filter_layout, filter_front } = params as Record<string, string>;
      if (!filter_selection) return;
      const isOurSelection = filter_selection === 'our_selection';
      if (filter_selection || filter_budget || filter_layout || filter_front || currentPage) {
        const sort = !isOurSelection ? filter_selection : undefined;
        const filters = `${sort}-${filter_budget}-${filter_layout}-${filter_front}`;
        if (!this.firstInit && this.filtersPrev && filters !== this.filtersPrev) {
          this.resetListing();
          return;
        }
        this.filtersPrev = filters;
        if (!this.firstInit && this.totalItems !== 0 && this.remainingItems <= 0) return;
        this.firstInit = false;
        this.loadKitchens({
          front: filter_front,
          layout: filter_layout,
          enableCustomBrandSelection: isOurSelection,
          sort,
          rangePrice: filter_budget,
          itemFrom,
          itemsToFetch,
        });
      }
    });
  }

  ngOnInit(): void {
    this.data.items.forEach((item) => {
      this.itemsToInsert.set(item.position, item);
    });
  }

  onReviewViewportEnter($event: boolean, kitchenId: string) {
    if ($event) {
      const fifthFromEnd = this.kitchens().at(-this.offsetFromEnd);
      if (this.nbKitchensLoaded < this.totalItems && !this.isAllKitchensLoaded) {
        if (fifthFromEnd?.id === kitchenId) {
          this.loadMore();
        }
      }
    }
  }

  loadMore() {
    this.urlParamsService.mergeParams({ listing_page: this.currentPageFromUrl() + 1 });
  }

  private loadKitchens(query: PayloadModels) {
    this.elasticSearchService
      .getKitchens({
        front: query.front,
        layout: query.layout,
        enableCustomBrandSelection: query.enableCustomBrandSelection,
        sort: query.sort,
        rangePrice: query.rangePrice,
        itemFrom: query.itemFrom,
        itemsToFetch: query.itemsToFetch,
      })
      .pipe(
        take(1),
        debounceTime(300),
        distinctUntilChanged(),
        tap((list: Pagination<Kitchen>) => {
          if (this.totalItems === 0) {
            this.totalItems = list.totalItems;
          }
          if (query.itemFrom === 0) {
            untracked(() => this.kitchens.set(list.items));
          } else {
            untracked(() => this.kitchens.update((kitchens) => [...kitchens, ...list.items]));
          }
          this.nbKitchensLoaded = untracked(() => this.kitchens().length);
          this.remainingItems = this.totalItems - this.nbKitchensLoaded;
          if (this.remainingItems <= 0) {
            this.isAllKitchensLoaded = true;
            this.logs.info('ListingModelesComponent', 'All kitchen models have been loaded', this.nbKitchensLoaded);
          }
          untracked(() => this.isExtendedSearch.set(list.isExtendedSearch ?? false));
        })
      )
      .subscribe({ error: (err) => this.logs.error('ListingModelesComponent', 'getKitchens failed', err) });
  }

  private resetListing() {
    this.firstInit = true;
    this.totalItems = 0;
    this.remainingItems = 0;
    this.nbKitchensLoaded = 0;
    this.isAllKitchensLoaded = false;
    this.urlParamsService.mergeParams({ listing_page: 1, reset: true });
  }
}
